import React from "react";
import { Stack, Typography } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { useQuest, usePromotion, useDuplicateQuest } from "@quest_editor/mocks";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import { getBuildOptionsDisplay } from "@quest_editor/utils/view/buildOptionsDisplay";
import PromotionInfoSection from "./PromotionInfoSection";
import SourceQuestInfoSection from "./SourceQuestInfoSection";
import DestinationSettingsSection from "./DestinationSettingsSection";

const QuestDuplication = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: questId } = useParams<{ id: string }>();

  const [newQuestName, setNewQuestName] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { duplicateQuest } = useDuplicateQuest();
  const { getTypeLabel } = getPromotionLabels();
  const { buildOptionsDisplay } = getBuildOptionsDisplay();

  React.useEffect(() => {
    if (!quest) return;
    setNewQuestName(quest.name);
  }, [quest]);

  const handleDuplicate = async () => {
    if (!questId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await duplicateQuest({ id: questId, newName: newQuestName });
      const newQuestId = result.data?.duplicateQuest.id;
      if (newQuestId) {
        navigate(`/quest_editor/quest/${newQuestId}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <PromotionInfoSection
        quest={quest}
        promotion={promotion}
        getTypeLabel={getTypeLabel}
        buildOptionsDisplay={buildOptionsDisplay}
      />

      <SourceQuestInfoSection quest={quest} />

      <DestinationSettingsSection
        newQuestName={newQuestName}
        setNewQuestName={setNewQuestName}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/quest/${quest.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("quest_duplication.button.back")}
        </BackButton>
        <ActionButton
          onClick={handleDuplicate}
          disabled={!newQuestName.trim() || isSubmitting}
        >
          {t("quest_duplication.button.duplication")}
        </ActionButton>
      </Stack>
    </Stack>
  );
};

export default QuestDuplication;
